import Vue from "vue";
import Router, { RouteConfig } from "vue-router";
import Layout from "@/layout/index.vue";
import LayoutSidebar from "@/layout/layoutSidebar.vue";
import EmptyLayout from "@/layout/emptyPage.vue";
import store from "./store";

Vue.use(Router);

/*
  redirect:                      if set to 'noredirect', no redirect action will be trigger when clicking the breadcrumb
  meta: {
    title: 'title'               the name showed in subMenu and breadcrumb (recommend set)
    icon: 'svg-name'             the icon showed in the sidebar
    breadcrumb: false            if false, the item will be hidden in breadcrumb (default is true)
    hidden: true                 if true, this route will not show in the sidebar (default is false)
  }
*/

function handleId(to:any, from:any, next:any) {
  if (to.params.id === ":id") {
    const workshopId = store.getters["user/workshopId"];
    next({ name: 'WorkshopDetailWorkshopUser', params: { id: workshopId } })
  }
  else next()
}

const isOneSnapEnabled = process.env.VUE_APP_ROOT_ONESNAP != null && process.env.VUE_APP_ROOT_ONESNAP != "";

const oneSnapRoute: RouteConfig = {
    path: "1-snap-app",
    name: "1SnapApp",
    component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/claim/1SnapApp.vue"),
    meta: {
        title: "1-Snap app",
        icon: "camera",
        menuType: "RewardsModule",
        isWorkshopScoped: true,
        claim: [
          {claimType: "ManageClaims", isWorkshopScoped: true},
        ]
    },
};

export default new Router({
  mode: "history",
  scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) {
      return savedPosition;
    } else {
      if (to.path != from.path) {
        return { x: 0, y: 0 };
      }
      
    }
  },
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/login",
      name: "Login",
      component: () => import(/* webpackChunkName: "login" */ "@/views/auth/Login.vue"),
      meta: {
        title: "Login",
        hidden: true,
      },
    },
    {
      path: "/register",
      name: "Register",
      component: () => import(/* webpackChunkName: "register" */ "@/views/auth/Register.vue"),
      meta: {
        title: "Register",
        hidden: true,
      },
    },
    {
      path: "/profile",
      component: LayoutSidebar,
      meta: {
        title: "My Profile",
      },
      children: [
        {
          path: "",
          name: "Profile",
          component: () => import(/* webpackChunkName: "profile" */ "@/views/auth/UserProfile.vue"),
          meta: {
            icon: "user",
            title: "My Profile",
            activeMenu: "/profile",
          },
        }
      ]
    },
    {
      path: "/confirm-email",
      name: "EmailVerification",
      component: () => import(/* webpackChunkName: "emailverification" */ "@/views/auth/EmailVerification.vue"),
      props: true,
      meta: {
        title: "Email Verification",
        hidden: true,
      }
    },
    {
      path: "/reset-password",
      name: "ForgotPassword",
      component: () => import(/* webpackChunkName: "passwordreset" */ "@/views/auth/ForgotPassword.vue"),
      props: true,
      meta: {
        title: "Password Reset",
        hidden: true,
      }
    },
    {
      path: "/404",
      name: "Error",
      component: () => import(/* webpackChunkName: "404" */ "@/views/404.vue"),
      meta: {
        title: "Page Not Found",
        hidden: true,
      },
    },
    {
      path: "/",
      component: Layout,
      redirect: "/",
      name: "LandingPage",
      meta: {
        title: "ServiceBay Portal",
      },
      children: [
        {
          path: "/",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/landing-page/index.vue"),
          meta: {
            title: "ServiceBay Portal",
            icon: "site",
            breadcrumb: false,
            menuType: "UserCRUD|WorkshopCRUD|RewardsModule",
          },
        },
      ],
    },
    {
      path: "/workshop-management",
      component: LayoutSidebar,
      meta: {
        title: "Workshops",
        menuType: "WorkshopCRUD",
        claim: [
          {claimType: "ManageWorkshops", isWorkshopScoped: false},
          {claimType: "ViewWorkshops", isWorkshopScoped: false}
        ]
      },
      children: [
        {
          path: "",
          name: "WorkshopManagement",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/workshop/List.vue"),
          meta: {
            title: "Workshop Management",
            icon: "officebuilding",
            menuType: "WorkshopCRUD",
            activeMenu: "/workshop-management",
            claim: [
              // {claimType: "ManageWorkshops", isWorkshopScoped: false},
              {claimType: "ViewWorkshops", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "workshop/:id",
          name: "WorkshopDetail",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/workshop/Detail.vue"),
          props: true,
          meta: {
            title: "Workshop Detail",
            menuType: "WorkshopCRUD",
            hidden: true,
            activeMenu: "/workshop-management",
            claim: [
              {claimType: "ManageWorkshops", isWorkshopScoped: true},
              {claimType: "ViewWorkshops", isWorkshopScoped: true}
            ]
          },
          // beforeEnter: claimCheck
        },
        {
          path: "workshop-detail/:id?",
          name: "WorkshopDetailWorkshopUser",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/workshop/Detail.vue"),
          props: true,
          meta: {
            title: "Workshop Detail",
            menuType: "WorkshopCRUD",
            activeMenu: "/workshop-management/workshop-detail/:id?",
            icon: "officebuilding",
            claim: [
              {claimType: "ManageWorkshops", isWorkshopScoped: true},
              {claimType: "ViewWorkshops", isWorkshopScoped: true}
            ]
          },
          beforeEnter: handleId
        },
        {
          path: "workshop",
          name: "NewWorkshop",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/workshop/Detail.vue"),
          meta: {
            title: "New Workshop",
            icon: "plus",
            menuType: "WorkshopCRUD",
            claim: [
              {claimType: "ManageWorkshops", isWorkshopScoped: false},
            ]
          },
        },
        {
          path: "business-hours",
          name: "BusinessHourSettings",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/workshop/BusinessHourSettings.vue"),
          meta: {
            title: "Business Hour Settings",
            icon: "calendar",
            menuType: "WorkshopCRUD",
            activeMenu: "/workshop-management/business-hours",
            // R4.3 requires the "ManageQuoteSettings" claim with a blank string value.
            // handleClaims() maps a blank claim value to the literal "super" sentinel,
            // and hasClaim(type, false) checks for exactly that - so isWorkshopScoped:
            // false IS the blank-value check. Matches /service-management Settings below.
            claim: [
              {claimType: "ManageQuoteSettings", isWorkshopScoped: false},
            ]
          },
        },
        {
          path: "workshop-users",
          name: "WorkshopUsers",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/workshop-users/List.vue"),
          meta: {
            title: "Workshop Users",
            icon: "user",
            menuType: "WorkshopUser",
            claim: [
              // {claimType: "ManageUsers", isWorkshopScoped: true}, // Not sure if this should be here...
              {claimType: "ViewUsers", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "workshop-user/:id",
          name: "WorkshopUserDetail",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/workshop-users/Detail.vue"),
          props: true,
          meta: {
            title: "Workshop User Detail",
            menuType: "WorkshopUser",
            hidden: true,
            activeMenu: "/workshop-management/workshop-users",
            claim: [
              {claimType: "ManageWorkshops", isWorkshopScoped: true},
              {claimType: "ViewWorkshops", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "delinquent-log",
          name: "DelinquentWorkshopLogs",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/workshop-users/DelinquentWorkshopLogs.vue"),
          meta: {
            title: "Delinquent Workshop Logs",
            icon: "warning",
            menuType: "WorkshopUser",
            claim: [
              {claimType: "ManageWorkshops", isWorkshopScoped: false},
              {claimType: "ViewWorkshops", isWorkshopScoped: false}
            ]
          },
        },
      ],
    },
    {
      path: "/user-management",
      component: LayoutSidebar,
      meta: {
        title: "Users",
        menuType: "UserCRUD",
        claim: [
          {claimType: "ManageUsers", isWorkshopScoped: false},
          {claimType: "ViewUsers", isWorkshopScoped: false}
        ]
      },
      children: [
        {
          path: "",
          name: "UserManagement",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/user/List.vue"),
          meta: {
            title: "User Management",
            icon: "user",
            menuType: "UserCRUD",
            activeMenu: "/user-management",
            claim: [
              // {claimType: "ManageUsers", isWorkshopScoped: false},
              {claimType: "ViewUsers", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "user/:id",
          name: "UserDetail",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/user/Detail.vue"),
          props: true,
          meta: {
            title: "User Detail",
            icon: "user",
            menuType: "UserCRUD",
            hidden: true,
            activeMenu: "/user-management",
            claim: [
              {claimType: "ManageUsers", isWorkshopScoped: false},
              {claimType: "ViewUsers", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "user",
          name: "NewUser",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/user/Detail.vue"),
          meta: {
            title: "New User",
            icon: "plus",
            menuType: "UserCRUD",
            claim: [
              {claimType: "ManageUsers", isWorkshopScoped: false},
            ]
          },
        },
      ],
    },
    {
      path: "/role-management",
      component: LayoutSidebar,
      meta: {
        title: "Roles",
        menuType: "UserCRUD",
        claim: [
          {claimType: "ManageUsers", isWorkshopScoped: false},
          {claimType: "ViewUsers", isWorkshopScoped: false}
        ]
      },
      children: [
        {
          path: "",
          name: "RoleManagement",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/roles/List.vue"),
          meta: {
            title: "Role Management",
            icon: "form",
            menuType: "UserCRUD",
            activeMenu: "/role-management",
            claim: [
              // {claimType: "ManageUsers", isWorkshopScoped: false},
              {claimType: "ViewUsers", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "role/:id",
          name: "RoleDetail",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/roles/Detail.vue"),
          props: true,
          meta: {
            title: "Role Details",
            icon: "user",
            menuType: "UserCRUD",
            hidden: true,
            activeMenu: "/role-management",
            claim: [
              {claimType: "ManageUsers", isWorkshopScoped: false},
              {claimType: "ViewUsers", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "role",
          name: "NewRole",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/roles/Detail.vue"),
          meta: {
            title: "New Role",
            icon: "plus",
            menuType: "UserCRUD",
            claim: [
              {claimType: "ManageUsers", isWorkshopScoped: false},
            ]
          },
        },
      ]
    },
    {
      path: "/rewards",
      component: LayoutSidebar,
      meta: {
        title: "My Rewards",
        menuType: "RewardsModule",
        claim: [
          {claimType: "ManageClaims", isWorkshopScoped: true},
          {claimType: "ViewClaims", isWorkshopScoped: true}
        ]
      },
      children: [
        {
          path: "",
          name: "RewardsModule",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/claim/Dashboard.vue"),
          meta: {
            title: "Dashboard",
            menuType: "RewardsModule",
            activeMenu: "/rewards",
            icon: "dashboard",
            breadcrumb: false,
            claim: [
              // {claimType: "ManageClaims", isWorkshopScoped: true},
              {claimType: "ViewClaims", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "upload-claim",
          name: "ClaimUpload",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/claim/Submit.vue"),
          meta: {
            title: "Upload Invoice",
            icon: "documentadd",
            menuType: "RewardsModule",
            claim: [
              {claimType: "ManageClaims", isWorkshopScoped: true},
              // {claimType: "ViewClaims", isWorkshopScoped: true}
            ]
          },
        },
        ...(isOneSnapEnabled ? [oneSnapRoute] : []),
        {
          path: "points-structure",
          component: () => import("@/views/reward-products/PointsStructure.vue"),
          name: "PointsStructure",
          meta: {
            title: "Points Structure",
            icon: "pricetag",
            menuType: "RewardsModule",
            isWorkshopScoped: true,
          },
        },
        {
          path: "points-structure/:brand",
          name: "BrandPointsStructure",
          component: () => import("@/views/reward-products/filtered/ProductListFiltered.vue"),
          props: true,
          meta: {
              title: "Product Points Structure",
              hidden: true,
              activeMenu: "/rewards/points-structure",
              menuType: "RewardsModule",
              isWorkshopScoped: true,
          },
        },
        {
          path: "upload-claim/:id",
          name: "ClaimReupload",
          props: true,
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/claim/Resubmit.vue"),
          meta: {
            title: "Submit Invoice",
            icon: "documentadd",
            menuType: "RewardsModule",
            hidden: true,
            activeMenu: "/rewards/claims",
            claim: [
              {claimType: "ManageClaims", isWorkshopScoped: true},
            ]
          },
        },
        {
          path: "claims",
          name: "ClaimList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/claim/List.vue"),
          props: true,
          meta: {
            title: "Rewards History",
            icon: "messagebox",
            menuType: "RewardsModule",
            claim: [
              // {claimType: "ManageClaims", isWorkshopScoped: true},
              {claimType: "ViewClaims", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "claim/:id",
          name: "ClaimDetail",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/claim/Detail.vue"),
          props: true,
          meta: {
            title: "Claim Detail",
            menuType: "RewardsModule",
            hidden: true,
            activeMenu: "/rewards/claims",
            claim: [
              {claimType: "ManageClaims", isWorkshopScoped: true},
              {claimType: "ViewClaims", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "gbucks/:id",
          name: "TransactionDetail",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/point-management/Detail.vue"),
          props: true,
          meta: {
            title: "GBucks Update Detail",
            menuType: "RewardsModule",
            hidden: true,
            activeMenu: "/rewards/claims",
            claim: [
              {claimType: "ManageClaims", isWorkshopScoped: true},
              {claimType: "ViewClaims", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "report",
          name: "RewardsReport",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/report/index.vue"),
          props: true,
          meta: {
            title: "Rewards Report",
            icon: "dataline",
            menuType: "RewardsModule",
            claim: [
              // {claimType: "ManageClaims", isWorkshopScoped: true},
              {claimType: "ViewClaims", isWorkshopScoped: true}
            ]
          },
        },
      ],
    },
    {
      path: "/reward-product-management",
      component: LayoutSidebar,
      meta: {
        title: "Reward Product Management",
        menuType: "RewardsModule",
        claim: [
          {claimType: "ManageProducts", isWorkshopScoped: false},
          {claimType: "ViewProducts", isWorkshopScoped: false}
        ]
      },
      children: [
        {
          path: "",
          name: "RewardProductList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/product/List.vue"),
          meta: {
            title: "Product List",
            icon: "files",
            menuType: "RewardsModule",
            activeMenu: "/reward-product-management",
            claim: [
              // {claimType: "ManageProducts", isWorkshopScoped: false},
              {claimType: "ViewProducts", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "product/:id",
          name: "RewardProductDetail",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/product/Detail.vue"),
          props: true,
          meta: {
            title: "Product Detail",
            menuType: "RewardsModule",
            activeMenu: "/reward-product-management",
            hidden: true
          },
        },
        {
          path: "product",
          name: "NewRewardProduct",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/product/Detail.vue"),
          meta: {
            title: "New Product",
            menuType: "RewardsModule",
            icon: "box",
            claim: [
              {claimType: "ManageProducts", isWorkshopScoped: false},
            ]
          },
        },
      ],
    },
    {
      path: "/gbucks-management",
      component: LayoutSidebar,
      meta: {
        title: "GBucks Management",
        menuType: "RewardsModule",
        claim: [
          {claimType: "ManageClaims", isWorkshopScoped: false},
          {claimType: "ViewClaims", isWorkshopScoped: false}
        ]
      },
      children: [
        {
          path: "",
          name: "RewardPointList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/point-management/List.vue"),
          meta: {
            title: "Claim Request List",
            icon: "list",
            menuType: "RewardsModule",
            activeMenu: "/gbucks-management",
            claim: [
              // {claimType: "ManageClaims", isWorkshopScoped: false},
              {claimType: "ViewClaims", isWorkshopScoped: false}
            ]
            // hidden: true
          },
        },
        {
          path: "review/:id",
          name: "ReviewClaimAdmin",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/point-management/Review.vue"),
          props: true,
          meta: {
            title: "Claim Review",
            menuType: "RewardsModule",
            hidden: true,
            activeMenu: "/gbucks-management",
            claim: [
              {claimType: "ManageClaims", isWorkshopScoped: false},
              {claimType: "ViewClaims", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "claim/:id",
          name: "ViewClaimAdmin",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/claim/Detail.vue"),
          props: true,
          meta: {
            title: "Claim Detail",
            menuType: "RewardsModule",
            hidden: true,
            activeMenu: "/gbucks-management",
            claim: [
              {claimType: "ManageClaims", isWorkshopScoped: false},
              {claimType: "ViewClaims", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "gbucks/:id",
          name: "ViewTransactionAdmin",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/point-management/Detail.vue"),
          props: true,
          meta: {
            title: "GBucks Update Detail",
            menuType: "RewardsModule",
            hidden: true,
            activeMenu: "/gbucks-management/reports",
            claim: [
              {claimType: "ManageClaims", isWorkshopScoped: false},
              {claimType: "ViewClaims", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "claim",
          name: "NewManualClaimAdmin",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/point-management/Create.vue"),
          meta: {
            title: "New Claim",
            menuType: "RewardsModule",
            icon: "documentadd",
            claim: [
              {claimType: "ManageClaims", isWorkshopScoped: false},
            ]
          },
        },
        {
          path: "manual-gbucks-update",
          name: "ManualPointUpdate",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/point-management/ManualPointUpdate.vue"),
          meta: {
            title: "Manual GBucks Update",
            menuType: "RewardsModule",
            icon: "form",
            claim: [
              {claimType: "ManageClaims", isWorkshopScoped: false},
              // {claimType: "ViewClaims", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "reports",
          name: "AdminRewardsReport",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/report/index.vue"),
          props: true,
          meta: {
            title: "Rewards Report",
            icon: "dataline",
            menuType: "RewardsModule",
            claim: [
              // {claimType: "ManageClaims", isWorkshopScoped: false},
              {claimType: "ViewClaims", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "returns",
          name: "AdminRewardsReturns",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/returns/List.vue"),
          props: true,
          meta: {
            title: "Rewards Returns",
            icon: "33,7962",
            menuType: "RewardsModule",
            claim: [
              // {claimType: "ManageReturns", isWorkshopScoped: false},
              {claimType: "ViewReturns", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "returns/:id",
          name: "ViewRewardsReturnAdmin",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/returns/Detail.vue"),
          props: true,
          meta: {
            title: "Rewards Return",
            menuType: "RewardsModule",
            hidden: true,
            activeMenu: "/gbucks-management/returns",
            claim: [
              {claimType: "ManageReturns", isWorkshopScoped: false},
              {claimType: "ViewReturns", isWorkshopScoped: false}
            ]
          },
        },
      ],
    },
    {
      path: "/services",
      component: LayoutSidebar,
      meta: {
        title: "Services",
        menuType: "ServiceModule",
        claim: [
          {claimType: "ManageQuotes", isWorkshopScoped: true}
        ]
      },
      children: [
        {
          path: "",
          name: "ServiceDashboard",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/Dashboard.vue"),
          meta: {
            title: "Service Dashboard",
            icon: "dataline",
            menuType: "ServiceModule",
            activeMenu: "/services",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "quote-list",
          name: "QuoteRequestList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/ServiceList.vue"),
          meta: {
            title: "Quote Requests",
            icon: "messagebox",
            menuType: "ServiceModule",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "quote-list/quote/:id",
          name: "ViewQuoteRequest",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/QuoteRequest.vue"),
          meta: {
            title: "Quote Details",
            menuType: "ServiceModule",
            hidden: true,
            activeMenu: "/services/quote-list",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: true}
            ]
          },
          props: true
        },
        {
          path: "booking-reschedule/:id",
          name: "BookingReschedule",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/BookingReschedule.vue"),
          meta: {
            title: "Booking Reschedule",
            menuType: "ServiceModule",
            hidden: true,
            activeMenu: "/services/quote-list",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "booking-list",
          name: "BookingList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/BookingList.vue"),
          meta: {
            title: "Service Bookings",
            icon: "calendar",
            menuType: "ServiceModule",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: true}
            ]
          },
        },
        {
          path: "booking-list/booking",
          name: "AdhocBooking",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/BookingDetailView.vue"),
          meta: {
            title: "New Adhoc Booking",
            icon: "plus",
            menuType: "ServiceModule",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: true}
            ]
          }
        },
        {
          path: "booking-list/booking/:id",
          name: "ViewBooking",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/BookingDetailView.vue"),
          meta: {
            title: "Booking Details",
            menuType: "ServiceModule",
            hidden: true,
            activeMenu: "/services/booking-list",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: true}
            ]
          },
          props: true
        },
        {
          path: "reviews",
          name: "WorkshopReviewList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/Reviews.vue"),
          meta: {
            title: "Workshop Reviews",
            icon: "star",
            menuType: "ServiceModule",
            claim: {claimType: "ManageQuotes", isWorkshopScoped: true}
          },
        },
        {
          path: "customers",
          name: "CustomerList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/CustomerList.vue"),
          meta: {
            title: "Customer List",
            icon: "users",
            menuType: "ServiceModule",
            claim: {claimType: "ManageQuotes", isWorkshopScoped: true}
          },
        },
      ]
    },
    {
      path: "/service-management",
      component: LayoutSidebar,
      meta: {
        title: "Services",
        menuType: "ServiceAdminModule",
        claim: [
          {claimType: "ManageQuotes", isWorkshopScoped: false},
          {claimType: "ManageQuoteSettings", isWorkshopScoped: false},
        ]
      },
      children: [
        {
          path: "settings",
          name: "ServiceMetadata",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/services-module/ServiceMetadata.vue"),
          meta: {
            title: "Settings",
            icon: "cog",
            menuType: "ServiceAdminModule",
            claim: [
              {claimType: "ManageQuoteSettings", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "",
          name: "AdminQuoteRequestList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/services-module/Dashboard.vue"),
          meta: {
            title: "Workshop Overview",
            icon: "dataline",
            menuType: "ServiceAdminModule",
            activeMenu: "/service-management",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: false}
            ]
          },
        },
        {
          path: "overview/:id",
          name: "ViewAdminQuoteOverview",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/services-module/QuoteOverview.vue"),
          meta: {
            title: "Request Detail",
            menuType: "ServiceAdminModule",
            hidden: true,
            activeMenu: "/service-management",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: false}
            ]
          },
          props: true
        },
        {
          path: "overview/quote/:id",
          name: "ViewAdminQuoteRequest",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/QuoteRequest.vue"),
          meta: {
            title: "Quote Details",
            menuType: "ServiceAdminModule",
            hidden: true,
            activeMenu: "/service-management",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: false}
            ]
          },
          props: true
        },
        // {
        //   path: "booking-list",
        //   name: "AdminBookingList",
        //   component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/promotions-bulletins/Detail.vue"),
        //   // component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/services-module/BookingList.vue"),
        //   meta: {
        //     title: "Booking Overview",
        //     icon: "calendar",
        //     menuType: "ServiceAdminModule",
        //     claim: [
        //       {claimType: "ManageQuotes", isWorkshopScoped: false}
        //     ]
        //   },
        // },
        {
          path: "booking-list/booking/:id",
          name: "ViewAdminBooking",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/BookingDetailView.vue"),
          meta: {
            title: "Booking Details",
            menuType: "ServiceAdminModule",
            hidden: true,
            activeMenu: "/service-management",
            claim: [
              {claimType: "ManageQuotes", isWorkshopScoped: false}
            ]
          },
          props: true
        },
        {
          path: "reviews",
          name: "AdminWorkshopReviewList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/services-module/Reviews.vue"),
          meta: {
            title: "Service Reviews",
            icon: "star",
            menuType: "ServiceAdminModule",
            claim: {claimType: "ManageQuotes", isWorkshopScoped: false}
          },
        },
        {
          path: "customers",
          name: "AdminCustomerList",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/services-module/CustomerList.vue"),
          meta: {
            title: "Service Customer List",
            icon: "users",
            menuType: "ServiceAdminModule",
            claim: {claimType: "ManageQuotes", isWorkshopScoped: false}
          },
        },
        {
          path: "audit-logs",
          name: "ServiceAuditLogs",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/auditing/ServiceLogs.vue"),
          meta: {
            title: "Service Audit Logs",
            icon: "files",
            menuType: "ServiceAdminModule",
            claim: {claimType: "ManageQuotes", isWorkshopScoped: false}
          },
        },
      ]
    },
    {
      path: "/audit-logs",
      component: LayoutSidebar,
      meta: {
        // title: "Audit Logs",
        menuType: "AuditingModule",
        claim: {claimType: "ViewAuditLogs", isWorkshopScoped: false}
      },
      children: [
        {
          path: "",
          name: "AuditLogs",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/auditing/index.vue"),
          meta: {
            title: "Audit Logs",
            icon: "files",
            menuType: "AuditingModule",
            activeMenu: "/audit-logs",
            claim: {claimType: "ViewAuditLogs", isWorkshopScoped: false}
          },
        },
      ]
    },
    {
      path: "/promotions-bulletins-management",
      component: LayoutSidebar,
      meta: {
        // title: "Promotions and Bulletins",
        menuType: "BulletinsAdminModule",
        claim: [
          {claimType: "ViewBulletins", isWorkshopScoped: false},
          {claimType: "ManageBulletins", isWorkshopScoped: false}
        ]
      },
      children: [
        {
          path: "",
          name: "AdminPromotionsBulletins",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/promotions-bulletins/List.vue"),
          meta: {
            title: "Bulletins",
            icon: "site",
            menuType: "BulletinsAdminModule",
            activeMenu: "/promotions-bulletins-management",
            claim: {claimType: "ViewBulletins", isWorkshopScoped: false}
          },
        },
        {
          path: "promotion-bulletin",
          name: "NewAdminPromotionBulletin",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/promotions-bulletins/Detail.vue"),
          meta: {
            title: "New Bulletin",
            icon: "plus",
            menuType: "BulletinsAdminModule",
            claim: {claimType: "ManageBulletins", isWorkshopScoped: false}
          },
        },
        {
          path: "promotion-bulletin/:id",
          name: "ViewAdminPromotionBulletin",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/management/promotions-bulletins/Detail.vue"),
          meta: {
            title: "Bulletin",
            menuType: "BulletinsAdminModule",
            hidden: true,
            activeMenu: "/promotions-bulletins-management",
            claim: [
              {claimType: "ViewBulletins", isWorkshopScoped: false},
              {claimType: "ManageBulletins", isWorkshopScoped: false}
            ]
          },
          props: true
        },
      ]
    },
    {
      path: "/promotions",
      component: EmptyLayout,
      meta: {
        // title: "Promotions",
        menuType: "PromotionsModule",
        claim: [
          {claimType: "ViewBulletins", isWorkshopScoped: true},
          {claimType: "ViewBulletins", isWorkshopScoped: false}
        ]
      },
      children: [
        {
          path: "",
          name: "Promotions",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/bulletins/List.vue"),
          meta: {
            title: "Promotions",
            icon: "site",
            menuType: "PromotionsModule",
            activeMenu: "/promotions",
            claim: {claimType: "ViewBulletins", isWorkshopScoped: true}
          },
        },
        {
          path: "promotion/:id",
          name: "ViewPromotion",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/bulletins/Detail.vue"),
          meta: {
            title: "Promotion",
            menuType: "PromotionsModule",
            hidden: true,
            activeMenu: "/promotions",
            claim: [
              {claimType: "ViewBulletins", isWorkshopScoped: true},
              {claimType: "ViewBulletins", isWorkshopScoped: false},
            ]
          },
          props: true
        },
      ]
    },
    {
      path: "/bulletins",
      component: EmptyLayout,
      meta: {
        // title: "Bulletins",
        menuType: "BulletinsModule",
        claim: [
          {claimType: "ViewBulletins", isWorkshopScoped: true},
          {claimType: "ViewBulletins", isWorkshopScoped: false}
        ]
      },
      children: [
        {
          path: "",
          name: "Bulletins",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/bulletins/List.vue"),
          meta: {
            title: "Bulletins",
            icon: "site",
            menuType: "BulletinsModule",
            activeMenu: "/bulletins",
            claim: {claimType: "ViewBulletins", isWorkshopScoped: true}
          },
        },
        {
          path: "bulletin/:id",
          name: "ViewBulletin",
          component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/bulletins/Detail.vue"),
          meta: {
            title: "Bulletin",
            menuType: "BulletinsModule",
            hidden: true,
            activeMenu: "/bulletins",
            claim: [
              {claimType: "ViewBulletins", isWorkshopScoped: true},
              {claimType: "ViewBulletins", isWorkshopScoped: false},
            ]
          },
          props: true
        },
      ]
    },
    {
      path: "/product-finder",
      name: "ProductFinder",
      component: () => import(/* webpackChunkName: "register" */ "@/views/product-finder/ProductFinder.vue"),
      meta: {
        title: "Product Finder",
        hidden: true,
      },
    },
    // {
    //   path: "/crm",
    //   component: LayoutSidebar,
    //   meta: {
    //     title: "Customer Relationship Management",
    //     menuType: "CRMModule",
    //   },
    //   children: [        
    //     {
    //       path: "customers",
    //       name: "CustomerList",
    //       component: () => import(/* webpackChunkName: "ServiceBay Portal" */ "@/views/rewards-module/claim/List.vue"),
    //       meta: {
    //         title: "Customer List",
    //         menuType: "RewardsModule"
    //       },
    //     },
    //   ]
    // },
    {
      path: "*",
      redirect: "/404",
      meta: { hidden: true },
    },
  ],
});
